export interface NotificationItem {
  id: number
  kind: string
  title: string
  body: string | null
  url: string
  notifiedAt: string
  readAt: string | null
}

export interface NotificationsResponse {
  notifications: NotificationItem[]
  nextCursor: string | null
  unreadCount: number
}

export interface NotificationsSummary {
  unreadCount: number
}

export interface LatestNotificationResponse {
  notification: NotificationItem | null
  unreadCount: number
}

const csrfToken = () =>
  document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ||
  ""

const jsonHeaders = {
  Accept: "application/json",
  "Content-Type": "application/json",
}

async function parseJson<Payload>(response: globalThis.Response): Promise<Payload> {
  return response.json() as Promise<Payload>
}

async function request<Payload>(
  url: string,
  options: RequestInit = {},
): Promise<Payload> {
  const response = await fetch(url, {
    credentials: "same-origin",
    ...options,
    headers: {
      ...jsonHeaders,
      ...options.headers,
    },
  })

  if (!response.ok) {
    throw new Error("Could not load notifications")
  }

  return parseJson<Payload>(response)
}

export const notificationQueryKeys = {
  summary: ["notifications", "summary"] as const,
  list: ["notifications", "list"] as const,
}

export const notificationsApi = {
  list(cursor?: string | null) {
    const params = cursor ? `?cursor=${encodeURIComponent(cursor)}` : ""

    return request<NotificationsResponse>(`/notifications${params}`)
  },

  summary() {
    return request<NotificationsSummary>("/notifications/summary")
  },

  markAllRead() {
    return request<NotificationsSummary>("/notifications/mark_all_read", {
      method: "PATCH",
      headers: {
        "X-CSRF-Token": csrfToken(),
      },
    })
  },

  markRead(ids: number[]) {
    return request<NotificationsSummary>("/notifications/mark_read", {
      method: "PATCH",
      body: JSON.stringify({ ids }),
      headers: {
        "X-CSRF-Token": csrfToken(),
      },
    })
  },

  latest() {
    return request<LatestNotificationResponse>("/notifications/latest")
  },
}
